import React, {useEffect, useState} from 'react'
import {useFormatDate} from '../../hooks/useFormatDate'
import {cx} from '../../utils'
import {ReactComponent as CloseIcon} from '../../assets/Icons/Close.svg'
import {WuMenu, WuMenuItem} from '@npm-questionpro/wick-ui-lib'
import styles from './DropdownComponent.module.scss'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../Translation/English/global'

export interface IDropdownItem {
  id?: string | number
  title?: string
  name?: string
  value?: string | number
  workforceName?: string
  goal?: string
  status?: string
  type?: string
  firstName?: string
  username?: string
  emailAddress?: string
  date?: string | Date
  NPS?: number | null
  combination?: string
  count?: number
}

interface IDropdownComponentProps {
  value?: string | React.ReactNode
  data?: IDropdownItem[]
  select: (item: IDropdownItem, index?: number) => void
  status?: string
  isBorderBottom?: boolean
  multiCount?: boolean
  disabled?: boolean
  disabledSelect?: boolean
  id?: string
  onReset?: () => void
  children?: React.ReactNode
  childrenHasValue?: boolean
  defaultValue?: string
  testId?: string
  extraProp?: boolean
  index?: number
  contentClassName?: string
  /** Popup alignment relative to the trigger. @default 'start' */
  menuAlign?: 'start' | 'center' | 'end'
  /** Class applied to WuMenu's own popup panel — the only hook that reaches it, since it portals to document.body rather than rendering under contentClassName. */
  menuClassName?: string
}

export const DropdownComponent: React.FC<IDropdownComponentProps> = ({
  value,
  data,
  select,
  status,
  isBorderBottom = true,
  multiCount = false,
  disabled = false,
  disabledSelect = false,
  id = 'dropdown',
  onReset,
  children,
  childrenHasValue = false,
  defaultValue,
  testId = '',
  extraProp = false,
  index,
  contentClassName,
  menuAlign = 'start',
  menuClassName,
}) => {
  const wt = useWt()
  const formatDate = useFormatDate()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [currentValue, setCurrentValue] = useState<string | undefined>(
    defaultValue,
  )

  const huddleClick = (item: IDropdownItem): void => {
    if (extraProp) select(item, index)
    else select(item)
    if (!disabled && !disabledSelect) {
      setCurrentValue(
        item?.title ||
          item?.name ||
          item?.value?.toString() ||
          item?.workforceName ||
          item?.goal ||
          item?.status ||
          item?.type,
      )
    }
    setIsOpen(false)
  }

  const resetClose = (): void => {
    if (onReset) {
      onReset()
    }
    setIsOpen(false)
    if (!disabled && !disabledSelect) {
      setCurrentValue(undefined)
    }
  }

  useEffect(() => {
    if (childrenHasValue) {
      setIsOpen(false)
    }
  }, [childrenHasValue, value])

  useEffect(() => {
    if (!defaultValue) {
      setCurrentValue('')
    }
  }, [defaultValue])

  return (
    <div
      data-testid={testId}
      id={id}
      className={cx(
        styles.dropdownContent,
        !isBorderBottom && styles.borderBottomNone,
        contentClassName,
      )}
    >
      <WuMenu
        open={isOpen}
        onOpenChange={(open: boolean) => {
          if (!disabled) setIsOpen(open)
        }}
        variant={isBorderBottom ? 'flat' : 'standard'}
        disabled={disabled}
        position={{align: menuAlign}}
        Placeholder={<span>{value || currentValue}</span>}
        className={cx(children && styles.customChild, menuClassName)}
      >
        {data?.length ? (
          data.map((item, itemIndex) => {
            return item?.NPS === null ? null : (
              <WuMenuItem
                key={itemIndex}
                id={item?.value?.toString().toLowerCase()}
                onClick={() => huddleClick(item)}
              >
                {status === 'init-status' ? (
                  <div className={styles.dropdownStatusBlock}>
                    <div
                      className={`status-icon  ${String(
                        item?.value,
                      ).toLowerCase()}-status`}
                    />
                    <span>{item?.name}</span>
                  </div>
                ) : (
                  <>
                    {multiCount ? (
                      <div className={styles.dropdownContentMultiCount}>
                        <span data-i18n-skip>
                          {item?.name}
                          {`(${item?.combination})`}
                        </span>
                        <span>{item?.count}</span>
                      </div>
                    ) : (
                      <div>
                        {item?.firstName ? (
                          <>{`${item?.username || item?.emailAddress}`}</>
                        ) : (
                          <>
                            {item?.title ||
                              item?.name ||
                              item?.value ||
                              item?.goal ||
                              item?.username ||
                              item?.emailAddress ||
                              item?.workforceName ||
                              item?.type ||
                              (item?.date &&
                                formatDate(item?.date, 'monthYear'))}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </WuMenuItem>
            )
          })
        ) : (
          <WuMenuItem preventCloseOnClick={!!children}>
            {children || <span>{wt(GLOBAL.noData)}</span>}
          </WuMenuItem>
        )}
      </WuMenu>
      {onReset && (currentValue || childrenHasValue) && (
        <CloseIcon
          width={10}
          height={10}
          className={styles.close}
          onClick={resetClose}
        />
      )}
    </div>
  )
}
